/**
 * Il testo di Otto, reso come lo leggerebbe una persona.
 *
 * Il modello a volte scrive in Markdown — asterischi per il grassetto,
 * trattini per gli elenchi, cancelletti per i titoli — e mostrarlo grezzo fa
 * sembrare rotta la risposta. Qui si interpreta il poco che serve (grassetto,
 * corsivo, codice, elenchi, titoli come righe in grassetto) e il resto resta
 * testo. Niente HTML dal modello: solo nodi React costruiti da noi.
 */

import type { ReactNode } from "react";

type Blocco =
  | { tipo: "paragrafo"; testo: string }
  | { tipo: "titolo"; testo: string }
  | { tipo: "elenco"; ordinato: boolean; voci: string[] };

/** Divide il testo in blocchi: paragrafi, titoli, elenchi. */
export function analizza(testo: string): Blocco[] {
  const righe = testo.replace(/\r\n/g, "\n").split("\n");
  const blocchi: Blocco[] = [];
  let paragrafo: string[] = [];
  let elenco: { ordinato: boolean; voci: string[] } | null = null;

  const chiudiParagrafo = () => {
    if (paragrafo.length > 0) {
      blocchi.push({ tipo: "paragrafo", testo: paragrafo.join(" ") });
      paragrafo = [];
    }
  };
  const chiudiElenco = () => {
    if (elenco) {
      blocchi.push({ tipo: "elenco", ...elenco });
      elenco = null;
    }
  };

  for (const grezza of righe) {
    const riga = grezza.trim();
    if (riga === "") {
      chiudiParagrafo();
      chiudiElenco();
      continue;
    }
    const titolo = /^#{1,6}\s+(.*)$/.exec(riga);
    if (titolo) {
      chiudiParagrafo();
      chiudiElenco();
      blocchi.push({ tipo: "titolo", testo: titolo[1]! });
      continue;
    }
    const puntato = /^[-*•]\s+(.*)$/.exec(riga);
    const numerato = /^\d+[.)]\s+(.*)$/.exec(riga);
    if (puntato || numerato) {
      chiudiParagrafo();
      const ordinato = Boolean(numerato);
      if (!elenco || elenco.ordinato !== ordinato) {
        chiudiElenco();
        elenco = { ordinato, voci: [] };
      }
      elenco.voci.push((puntato ?? numerato)![1]!);
      continue;
    }
    chiudiElenco();
    paragrafo.push(riga);
  }
  chiudiParagrafo();
  chiudiElenco();
  return blocchi;
}

/**
 * Grassetto, corsivo e codice dentro una riga.
 *
 * Un asterisco spaiato resta un asterisco: meglio mostrare il carattere che
 * mangiarsi mezza frase.
 */
function inline(testo: string): ReactNode[] {
  const nodi: ReactNode[] = [];
  const schema =
    /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`|\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g;
  let ultimo = 0;
  let chiave = 0;
  for (const m of testo.matchAll(schema)) {
    const inizio = m.index ?? 0;
    if (inizio > ultimo) nodi.push(testo.slice(ultimo, inizio));
    const pezzo = m[0];
    if (pezzo.startsWith("**") || pezzo.startsWith("__")) {
      nodi.push(<b key={chiave++}>{pezzo.slice(2, -2)}</b>);
    } else if (pezzo.startsWith("`")) {
      nodi.push(
        <code
          key={chiave++}
          className="rounded-[4px] bg-ink/8 px-1 py-px text-[0.92em]"
        >
          {pezzo.slice(1, -1)}
        </code>,
      );
    } else {
      nodi.push(<i key={chiave++}>{pezzo.slice(1, -1)}</i>);
    }
    ultimo = inizio + pezzo.length;
  }
  if (ultimo < testo.length) nodi.push(testo.slice(ultimo));
  return nodi;
}

export function Prose({ text, cursor }: { text: string; cursor?: boolean }) {
  const blocchi = analizza(text);
  const cursore = cursor ? (
    <span className="pulse-dot ml-0.5 inline-block h-[1.05em] w-[2px] translate-y-[2px] rounded-full bg-accent align-baseline" />
  ) : null;

  if (blocchi.length === 0) return <p>{cursore}</p>;

  return (
    <>
      {blocchi.map((b, i) => {
        const ultimo = i === blocchi.length - 1;
        const spazio = i > 0 ? "mt-2" : undefined;
        if (b.tipo === "titolo") {
          return (
            <p key={i} className={`${spazio ?? ""} font-extrabold`.trim()}>
              {inline(b.testo)}
              {ultimo ? cursore : null}
            </p>
          );
        }
        if (b.tipo === "elenco") {
          const Tag = b.ordinato ? "ol" : "ul";
          return (
            <Tag
              key={i}
              className={`${spazio ?? ""} flex flex-col gap-1 pl-5 ${b.ordinato ? "list-decimal" : "list-disc"} marker:text-ink-faint`.trim()}
            >
              {b.voci.map((v, j) => (
                <li key={j}>
                  {inline(v)}
                  {ultimo && j === b.voci.length - 1 ? cursore : null}
                </li>
              ))}
            </Tag>
          );
        }
        return (
          <p key={i} className={spazio}>
            {inline(b.testo)}
            {ultimo ? cursore : null}
          </p>
        );
      })}
    </>
  );
}
